import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router, RouterStateSnapshot } from '@angular/router';
import { KeycloakService } from 'keycloak-angular';
import { BehaviorSubject, catchError, filter, map, of, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Account, BEAccount, Role } from '../../../interfaces/account';
import { PraticaType, StatoPratica } from '../../../interfaces/pratica';
import { ChannelType } from '../../settings/canals/channel-type';
import { StoreData } from '../models/store-data';
import { Opening } from '../../opening/opening';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly queryEndpoint = `${environment.api}/user-query/user`;
  private readonly commandEndpoint = `${environment.api}/user-command/user`;

  private _me = new BehaviorSubject<StoreData<BEAccount>>(new StoreData());
  me$ = this._me.asObservable();

  private _opening = new BehaviorSubject<StoreData<Opening>>(new StoreData());

  opening$ = this._opening.asObservable();

  constructor(
    public router: Router,
    protected readonly keycloak: KeycloakService,
    private http: HttpClient
  ) {}

  logout() {
    this.keycloak.logout();
  }

  keycloakLogin(routerState: RouterStateSnapshot) {
    let url = window.location.origin + '/';

    return this.keycloak.login({
      redirectUri: url + routerState.url,
    });
  }

  getMe() {
    this._me.next(this._me.value.update('waiting'));
    return this.http.get<BEAccount>(`${this.queryEndpoint}/me`).pipe(
      tap((me) => this._me.next(this._me.value.update('completed', me))),
      //map((me) => me),
      catchError((error) => {
        /* if (error.status === 401) {
          this.router.navigateByUrl('/');
        } */
        this._me.next(this._me.value.update('error'));
        return of(undefined);
      })
    );
  }
  edit(updateAccountData: Partial<Account>, id: string) {
    let body: Partial<Account> = {
      ...updateAccountData,
      id,
    };
    return this.http.patch(this.commandEndpoint + '/' + id, body);
  }

  get me() {
    return Object.freeze(this._me.value.data);
  }

  get isAdmin$() {
    return this.me$.pipe(
      filter((storeData) => storeData.state === 'completed'),
      map((storeData) => {
        let isAdmin = storeData.data!.ruoli.includes('Amministratore');
        return isAdmin;
      })
    );
  }

  get isResponsabile() {
    let isResponsabile = this.me?.ruoli.includes('Responsabile');
    return isResponsabile || false;
  }

  get isAddetto() {
    let isAddetto = this.me?.ruoli.includes('Addetto');
    return isAddetto || false;
  }

  get isFundManager() {
    let isFundManager = this.me?.ruoli.includes('Fund manager');
    return isFundManager || false;
  }

  get isAmministratore() {
    let isAmministratore = this.me?.ruoli.includes('Amministratore');
    return isAmministratore || false;
  }

  get isCdc() {
    let isCdc = this.me?.groups.includes(ChannelType.CENTROCOMPENTENZA);
    return isCdc || false;
  }

  get isSgr() {
    let isSgr = this.me?.groups.includes(ChannelType.SGR);
    return isSgr || false;
  }

  get isAml() {
    let isAml = this.me?.groups.includes(ChannelType.AML);
    return isAml || false;
  }

  getAvailablePraticaStates(praticaType?: PraticaType) {
    const statiPraticaRecord: Record<Role, StatoPratica[]> = {
      Addetto: [StatoPratica.APERTA, StatoPratica.COMPLETATA],
      Responsabile: [StatoPratica.APERTA, StatoPratica.COMPLETATA],
      'Back office': [StatoPratica.APERTA, StatoPratica.COMPLETATA],
      Funzione: [StatoPratica.COMPLETATA],
      Autorizzatore: [StatoPratica.IN_AUTORIZZAZIONE],

      'Fund manager': [
        StatoPratica.IN_VALUTAZIONE_FUND,
        StatoPratica.IN_DELIBERA_CDA,
      ],
      Amministratore: [
        StatoPratica.IN_VALUTAZIONE_FUND,
        StatoPratica.IN_DELIBERA_CDA,
      ],
    };
    const role = this.me!.ruoli[0];

    return statiPraticaRecord[role] || [];
  }
}
